import React, { useEffect, useState } from "react";
import { Button, Card, DatePicker, Descriptions, Input, Modal, Select, Space, Table, Tag, Timeline, message } from "antd";
import {
    ReloadOutlined,
    EyeOutlined,
    NodeIndexOutlined,
    PrinterOutlined,
    CloseCircleOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { useBts } from "./BtsStore";
import { firstDefined, money, statusColor, toOptions } from "./helpers";

const { RangePicker } = DatePicker;

const displayValue = (value) => {
    if (value === undefined || value === null || value === "") return "—";
    if (typeof value !== "object") return String(value);
    const preferred = firstDefined(value, ["info", "name", "title", "code", "id"]);
    return preferred !== undefined && typeof preferred !== "object"
        ? String(preferred)
        : JSON.stringify(value);
};

const JsonBlock = ({ data }) => (
    <pre style={{
        background: "var(--bg-main)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: 14,
        maxHeight: "55vh",
        overflow: "auto",
        fontSize: 12,
        margin: 0,
    }}>
        {JSON.stringify(data, null, 2)}
    </pre>
);

const BtsOrders = () => {
    const { orders, loading, getOrders, getOrder, trackOrder, getSticker, cancelOrder, statuses, getStatuses } = useBts();

    const [filters, setFilters] = useState({ search: "", status: undefined, range: null });
    const [detail, setDetail] = useState(null);
    const [track, setTrack] = useState(null);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        getOrders();
        getStatuses();
    }, [getOrders, getStatuses]);

    const applyFilters = () => {
        const params = {};
        if (filters.search) params.search = filters.search;
        if (filters.status !== undefined) params.status = filters.status;
        if (filters.range?.length === 2) {
            params.date_from = filters.range[0].format("YYYY-MM-DD");
            params.date_to = filters.range[1].format("YYYY-MM-DD");
        }
        getOrders(params);
    };

    const openDetail = async (rec) => {
        const id = firstDefined(rec, ["orderId", "id"]);
        try {
            setBusyId(id);
            const data = await getOrder(id);
            setDetail({ id, data });
        } catch (e) {
            message.error("Buyurtma ma'lumotini olishda xatolik");
        } finally {
            setBusyId(null);
        }
    };

    const openTrack = async (rec) => {
        const id = firstDefined(rec, ["orderId", "id"]);
        try {
            setBusyId(id);
            const data = await trackOrder(id);
            setTrack({ id, data });
        } catch (e) {
            message.error("Holatni olishda xatolik");
        } finally {
            setBusyId(null);
        }
    };

    const openSticker = async (rec) => {
        const id = firstDefined(rec, ["orderId", "id"]);
        try {
            setBusyId(id);
            const data = await getSticker(id);
            const url = typeof data === "string" ? data : firstDefined(data, ["url", "link", "sticker", "file"]);
            if (url && /^https?:\/\//.test(url)) {
                window.open(url, "_blank");
            } else if (typeof data === "string" && data.includes("<")) {
                const w = window.open("", "_blank");
                w.document.write(data);
            } else {
                Modal.info({ title: "Sticker", width: 700, content: <JsonBlock data={data} /> });
            }
        } catch (e) {
            message.error("Stickerni olishda xatolik");
        } finally {
            setBusyId(null);
        }
    };

    const confirmCancel = (rec) => {
        const id = firstDefined(rec, ["orderId", "id"]);
        Modal.confirm({
            title: `#${id} buyurtmani bekor qilish`,
            content: "Buyurtmani bekor qilishni tasdiqlaysizmi? (faqat yangi holatdagi buyurtmalar)",
            okText: "Ha, bekor qilish",
            okButtonProps: { danger: true },
            cancelText: "Yo'q",
            onOk: async () => {
                try {
                    await cancelOrder(id);
                    message.success("Buyurtma bekor qilindi");
                    applyFilters();
                } catch (e) {
                    message.error(e?.response?.data?.message || "Bekor qilishda xatolik");
                }
            },
        });
    };

    const columns = [
        {
            title: "ID",
            width: 90,
            render: (_, r) => <b>#{firstDefined(r, ["orderId", "id", "order_id"]) ?? "—"}</b>,
        },
        {
            title: "Kod",
            render: (_, r) => displayValue(firstDefined(r, ["wcs_code", "clientId", "client_id", "barcode"])),
        },
        {
            title: "Qabul qiluvchi",
            render: (_, r) => {
                const name = firstDefined(r, ["receiverName", "receiver_name"]) ?? r?.receiver?.name;
                const phone = firstDefined(r, ["receiverPhone", "receiver_phone"]) ?? r?.receiver?.phone;
                return <div>{displayValue(name)}<br /><span style={{ color: "var(--text-muted)", fontSize: 12 }}>{displayValue(phone === undefined ? "" : phone)}</span></div>;
            },
        },
        {
            title: "Holat",
            width: 150,
            render: (_, r) => {
                const status = firstDefined(r, ["status_code", "statusCode", "status"]);
                const code = typeof status === "object" ? firstDefined(status, ["code", "id", "status_code"]) : status;
                const name = firstDefined(r, ["status_name", "statusName"]) ?? (typeof status === "object" ? firstDefined(status, ["info", "name", "title", "code"]) : status);
                return <Tag color={statusColor(code)}>{displayValue(name)}</Tag>;
            },
        },
        {
            title: "Narx",
            width: 130,
            align: "right",
            render: (_, r) => {
                const p = firstDefined(r, ["price", "summaryPrice", "cost", "amount"]);
                return p != null ? `${money(p)} so'm` : "—";
            },
        },
        {
            title: "Sana",
            width: 150,
            render: (_, r) => displayValue(firstDefined(r, ["created_at", "createdAt", "senderDate", "date"])),
        },
        {
            title: "Amallar",
            fixed: "right",
            width: 170,
            align: "center",
            render: (_, r) => (
                <Space size="small">
                    <Button type="text" icon={<EyeOutlined />} loading={busyId === firstDefined(r, ["orderId", "id"])} onClick={() => openDetail(r)} title="Batafsil" />
                    <Button type="text" icon={<NodeIndexOutlined />} style={{ color: "var(--primary)" }} onClick={() => openTrack(r)} title="Holat" />
                    <Button type="text" icon={<PrinterOutlined />} onClick={() => openSticker(r)} title="Sticker" />
                    <Button type="text" danger icon={<CloseCircleOutlined />} onClick={() => confirmCancel(r)} title="Bekor qilish" />
                </Space>
            ),
        },
    ];

    const trackItems = (() => {
        const d = track?.data;
        const arr = Array.isArray(d) ? d : (d?.history || d?.tracking || d?.events || d?.steps || []);
        if (!Array.isArray(arr) || !arr.length) return null;
        return arr.map((it, i) => ({
            color: statusColor(firstDefined(it, ["status_code", "statusCode", "status"])),
            children: (
                <div key={i}>
                    <b>{displayValue(firstDefined(it, ["status_name", "statusName", "status", "name"]))}</b>
                    <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                        {displayValue(firstDefined(it, ["date", "created_at", "createdAt", "time"]))}
                        {firstDefined(it, ["city", "cityName", "location"]) ? ` · ${displayValue(firstDefined(it, ["city", "cityName", "location"]))}` : ""}
                    </div>
                    {firstDefined(it, ["comment", "note", "description"]) && (
                        <div style={{ fontSize: 12 }}>{displayValue(firstDefined(it, ["comment", "note", "description"]))}</div>
                    )}
                </div>
            ),
        }));
    })();

    return (
        <div>
            <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap>
                    <Input
                        allowClear
                        prefix={<SearchOutlined style={{ color: "var(--text-muted)" }} />}
                        placeholder="Kod / clientId bo'yicha qidirish"
                        style={{ width: 240 }}
                        value={filters.search}
                        onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                        onPressEnter={applyFilters}
                    />
                    <Select
                        allowClear
                        style={{ width: 200 }}
                        placeholder="Holat bo'yicha"
                        value={filters.status}
                        onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
                        options={toOptions(statuses, ["code", "id", "status_code"], ["name", "nameuz", "title"])}
                    />
                    <RangePicker value={filters.range} onChange={(v) => setFilters((f) => ({ ...f, range: v }))} />
                    <Button type="primary" icon={<SearchOutlined />} onClick={applyFilters}>Filtrlash</Button>
                    <Button icon={<ReloadOutlined />} onClick={() => { setFilters({ search: "", status: undefined, range: null }); getOrders(); }}>
                        Yangilash
                    </Button>
                </Space>
            </Card>

            <Table
                rowKey={(r) => firstDefined(r, ["orderId", "id"]) ?? Math.random()}
                loading={loading}
                columns={columns}
                dataSource={orders}
                size="small"
                scroll={{ x: 1000 }}
                pagination={{ pageSize: 20, showTotal: (t) => `Jami: ${t}` }}
                expandable={{
                    expandedRowRender: (r) => <JsonBlock data={r} />,
                }}
            />

            {/* Batafsil */}
            <Modal
                open={!!detail}
                onCancel={() => setDetail(null)}
                title={`Buyurtma #${detail?.id} — batafsil`}
                footer={null}
                width="70%"
            >
                {detail?.data && typeof detail.data === "object" && !Array.isArray(detail.data) ? (
                    <>
                        <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                            {Object.entries(detail.data)
                                .filter(([, v]) => typeof v !== "object")
                                .map(([k, v]) => (
                                    <Descriptions.Item key={k} label={k}>{String(v)}</Descriptions.Item>
                                ))}
                        </Descriptions>
                        <JsonBlock data={detail.data} />
                    </>
                ) : (
                    <JsonBlock data={detail?.data} />
                )}
            </Modal>

            {/* Holat (tracking) */}
            <Modal
                open={!!track}
                onCancel={() => setTrack(null)}
                title={`Buyurtma #${track?.id} — holat`}
                footer={null}
                width="60%"
            >
                {trackItems ? <Timeline items={trackItems} /> : <JsonBlock data={track?.data} />}
            </Modal>
        </div>
    );
};

export default BtsOrders;
